import type { components } from "@frend-digital/centra-types/oas";
import {
  type BaseSelection,
  type BaseSelectionItem,
  type BaseSelectionResponse,
} from "@frend-digital/centra-types/selection";
import type { DataEvent } from "../../client";
import type { Adapter } from "../types";
import { gtag, type BeforeEvent, type GTMProduct } from "./gtm-client";

export type CentraGTMFormatter = (product: BaseSelectionItem) => GTMProduct;

export function selectionItemToGTAGProduct(
  item: BaseSelectionItem
): GTMProduct {
  const categories = item.product?.categoryName
    ? Object.fromEntries(
        item.product.categoryName
          .map((name, i) => [`item_category${i === 0 ? "" : i + 1}`, name])
          .slice(0, 4)
      )
    : {};
  return {
    item_id: item.sku!,
    item_name: item.product?.name!,
    price: item.priceEachAsNumber!,
    quantity: item.quantity,
    item_brand: item.product?.brand,
    ...categories,
    item_variant: item.product?.variantName,
  };
}

export const createGTMCentra = ({
  beforeEvent,
  formatter = selectionItemToGTAGProduct,
}: { beforeEvent?: BeforeEvent; formatter?: CentraGTMFormatter } = {}) => ({
  addToCart: (selection: BaseSelection, itemId: string) => {
    gtag.addToCart(
      {
        currency: selection.currency!,
        value: selection.totals?.grandTotalPriceAsNumber!,
        items: selection.items?.map(formatter)!,
      },
      beforeEvent
    );
  },

  viewCart: (selection: BaseSelection) => {
    gtag.viewCart(
      {
        currency: selection.currency!,
        value: selection.totals?.grandTotalPriceAsNumber!,
        items: selection.items?.map(formatter)!,
      },
      beforeEvent
    );
  },

  removeFromCart: (selection: BaseSelection, itemId: string) => {
    gtag.removeFromCart(
      {
        currency: selection.currency!,
        value: selection.totals?.grandTotalPriceAsNumber!,
        items: selection.items?.map(formatter)!,
      },
      beforeEvent
    );
  },

  addToWishlist: (selection: BaseSelection, itemId: string) => {
    gtag.addToWishlist(
      {
        currency: selection.currency!,
        value: selection.totals?.grandTotalPriceAsNumber!,
        items: selection.items?.map(formatter)!,
      },
      beforeEvent
    );
  },

  beginCheckout: (selection: BaseSelection) => {
    gtag.beginCheckout(
      {
        currency: selection.currency!,
        value: selection.totals?.grandTotalPriceAsNumber!,
        items: selection.items?.map(formatter)!,
      },
      beforeEvent
    );
  },
  addPaymentInfo: (selection: BaseSelection, paymentMethod: string) => {
    gtag.addPaymentInfo(
      {
        currency: selection.currency!,
        value: selection.totals?.grandTotalPriceAsNumber!,
        payment_type: paymentMethod,
        items: selection.items?.map(formatter)!,
      },
      beforeEvent
    );
  },
  shipping: (selection: BaseSelection, shippingMethod?: string) => {
    gtag.shipping(
      {
        currency: selection.currency!,
        value: selection.totals?.grandTotalPriceAsNumber!,
        items: selection.items?.map(formatter)!,
        shipping_tier: shippingMethod || selection.shippingMethodName!,
      },
      beforeEvent
    );
  },
  purchase: (order: components["schemas"]["OrderCompleteModel"]) => {
    const tax = order.totals?.grandTotalPriceTaxAsNumber;
    gtag.purchase(
      {
        currency: order.currency!,
        value: order.totals?.grandTotalPriceAsNumber!,
        transaction_id: order.order!,
        items: order.items?.map(formatter)!,
        tax,
        shipping: order.totals?.shippingPriceAsNumber,
        coupon: order.discounts?.vouchers?.map((v) => v.voucher)?.join(";"),
      },
      beforeEvent
    );
  },
});

type CentraSelectionEvent = {
  type: "frd-event-type";
  event:
    | "view_cart"
    | "add_to_cart"
    | "remove_from_cart"
    | "add_to_wishlist"
    | "begin_checkout"
    | "select_item"
    | "select_product_size"
    | "add_shipping_info"
    | "add_payment_info";
  data: BaseSelectionResponse;
  additionalData: {
    dispatcher: "centra";
    item_id: string;
    [key: string]: any;
  };
};

type CentraPurchaseEvent = {
  type: "frd-event-type";
  event: "purchase";
  data: components["schemas"]["OrderCompleteModel"];
  additionalData: {
    dispatcher: "centra";
    [key: string]: any;
  };
};

export type CentraEvent = CentraSelectionEvent | CentraPurchaseEvent;

export const isCentraEvent = (e: DataEvent["detail"]): e is CentraEvent => {
  return (
    "type" in e &&
    e.type === "frd-event-type" &&
    e?.additionalData?.dispatcher === "centra"
  );
};

/**
 * Handles any events that are dispatched from the Centra API
 * Assumes GTM is initialized via @next/third-parties
 */
export const centraGTMAdapter = ({
  beforeEvent,
  formatter = selectionItemToGTAGProduct,
  selection,
}: {
  beforeEvent?: BeforeEvent;
  formatter?: CentraGTMFormatter;
  selection?: BaseSelectionResponse;
} = {}) => {
  return <Adapter>{
    consume: (e) => {
      if (!isCentraEvent(e)) return;

      const gtmCentra = createGTMCentra({
        beforeEvent,
        formatter,
      });

      try {
        switch (e.event) {
          case "view_cart":
            gtmCentra.viewCart(e.data.selection!);
            break;
          case "add_to_cart":
            gtmCentra.addToCart(e.data.selection!, e.additionalData.item_id);
            break;
          case "remove_from_cart":
            gtmCentra.removeFromCart(
              e.data.selection!,
              e.additionalData.item_id
            );
            break;
          case "add_to_wishlist":
            gtmCentra.addToWishlist(
              e.data.selection!,
              e.additionalData.item_id
            );
            break;
          case "begin_checkout":
            gtmCentra.beginCheckout(e.data.selection!);
            break;
          case "purchase":
            gtmCentra.purchase(e.data as any);
            break;
          case "select_item":
            console.log("Event not built for select_item");
            break;
          case "add_shipping_info":
            gtmCentra.shipping(
              e.data.selection!,
              e.additionalData.shipping_method
            );
            break;
          case "add_payment_info":
            const paymentMehtod = (e.data as any)?.paymentMethod;
            if (selection?.selection && paymentMehtod) {
              gtmCentra.addPaymentInfo(selection.selection, paymentMehtod);
            }
        }
      } catch (error) {
        console.error("Failed to push event to GTM", error);
      }
    },
  };
};
